import React, { useState, useRef, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext } from 'react-router-dom';
import ReactMarkdown from 'react-markdown'; // Importamos la librería de Markdown
import axios from 'axios';

import './RoomsSection.css';
import { routeMap } from '../../../config/routes';

// Interfaz actualizada
interface RoomSummary {
  id: number;
  title: string;
  slug: string;
  description: string;
  summary: string;
  images: { url: string }[];
}

interface BookingContext {
  openBooking: () => void;
}

const RoomsSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split('-')[0] as 'es' | 'en';
  const { openBooking } = useOutletContext<BookingContext>();

  const [rooms, setRooms] = useState<RoomSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentRoomIndex, setCurrentRoomIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const API_URL = process.env.REACT_APP_API_URL || 'https://admin.villasflamingos.com/api';

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const response = await axios.get(`${API_URL}/rooms`, {
          headers: { 'Accept-Language': currentLang }
        });

        if (response.data && response.data.data && response.data.data.length > 0) {
          setRooms(response.data.data);
        }
      } catch (error) {
        console.error("Error al cargar habitaciones en el slider:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRooms();
  }, [currentLang, API_URL]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(entry => {
        if (entry.isIntersecting) setVisible(true);
      }),
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
  }, [loading]);

  const goToNextRoom = () => {
    if (rooms.length === 0) return;
    setCurrentRoomIndex((prev) => (prev + 1) % rooms.length);
  };

  const goToPrevRoom = () => {
    if (rooms.length === 0) return;
    setCurrentRoomIndex((prev) => (prev - 1 + rooms.length) % rooms.length);
  };

  const getRoomLink = (slug: string) => {
    const routePattern = routeMap.roomDetail[currentLang];
    const path = routePattern.replace(':slug', slug);
    return `/${currentLang}/${path}`;
  };

  if (loading || rooms.length === 0) return null;

  const currentRoom = rooms[currentRoomIndex];
  const firstImage = currentRoom.images?.[0]?.url || '';
  const detailLink = getRoomLink(currentRoom.slug);

  return (
    <section
      ref={sectionRef}
      className={`rooms-section ${visible ? "animate-in" : "animate-out"}`}
    >
      <div className={`rooms-header-title ${visible ? "animate-in" : "animate-out"}`}>
        <h2 className="section-title">{t("rooms:title")}</h2>
      </div>

      <div className="room-carousel-item">
        <button onClick={goToPrevRoom} className="nav-arrow-external">
          <FiChevronLeft size={40} />
        </button>

        <div className="carousel-main-container">
          <div className="room-image-container">
            {firstImage && (
              <Link to={detailLink}>
                <img
                  src={firstImage}
                  className="room-image animate-in"
                  alt={currentRoom.title}
                  key={firstImage}
                />
              </Link>
            )}
          </div>
        </div>

        <div
          key={currentRoom.id + "-info"}
          className={`room-info ${visible ? "animate-in" : "animate-out"}`}
        >
          <Link to={detailLink} className="no-underline text-inherit">
            <h3 className="room-name cursor-pointer hover:opacity-80 transition-opacity">
              {currentRoom.title}
            </h3>
          </Link>

          {/* CAMBIO: Aplicamos ReactMarkdown al summary */}
          <div className="room-summary-markdown line-clamp-4">
            <ReactMarkdown>
              {currentRoom.summary}
            </ReactMarkdown>
          </div>

          <button
            onClick={openBooking}
            className="reserve-button"
            style={{ cursor: 'pointer' }}
          >
            <span></span>
            {t("rooms:reserve")}
          </button>
        </div>

        <button onClick={goToNextRoom} className="nav-arrow-external">
          <FiChevronRight size={40} />
        </button>
      </div>

      <div className="linea-global linea-separator-bottom"></div>
    </section>
  );
};

export default RoomsSection;